import { apiClient } from './client';

/**
 * Session attachment — video / audio file uploaded against a
 * capture-session. Lives in `tenant_<slug>.session_attachments`
 * (per-tenant revision 14). Two upload surfaces:
 *
 *   - Tenant admin: /tenants/{tid}/sessions/{sid}/attachments
 *   - Participant:  /sessions/me/{sid}/attachments
 *
 * Two-step upload flow shared by both surfaces:
 *
 *   1. POST create → backend mints row + returns presigned PUT URL
 *   2. Client PUTs the bytes directly to S3
 *   3. POST confirm → backend stamps uploaded_at on the row
 */
/** Kinds an operator can upload from the portal. `coaching` is a coaching
 *  session (debrief) recorded after the procedure — video or audio, on its
 *  own timeline, transcribed in full (COACHING-SESSIONS). */
export type AttachmentKind = 'video' | 'audio' | 'coaching';

/** Kinds the API can *return* — includes the device-only session
 *  anchor-audio (the capture-app's silent EEG-sync recording), which the
 *  portal never uploads but can play back for the ±5 s megaphone. */
export type AttachmentKindRead = AttachmentKind | 'audio_session_anchor';

/**
 * Per-marker video-frame preview the worker pod produced — one row
 * per peak/valley extremum on the cognitive trace. Surfaced by the
 * Cognitive trace card's hover preview. ROADMAP — peak/valley
 * video frames.
 */
export interface AttachmentThumb {
  id: string;
  /** Session-elapsed seconds matching the trace card's x-axis. */
  marker_time_s: number;
  phase: string;
  kind: 'peak' | 'valley';
  focus_value: number;
  /** Short-lived presigned GET URL. The portal `<img>`s it directly;
   *  if it expires the next refresh of this list picks up a fresh one. */
  presigned_get_url: string;
  /** ROADMAP TRACE-FRAMES-ALIGN — seconds between the video's
   *  actual start and `session.started_at`. Positive = video
   *  started AFTER EEG; negative = video pre-dates EEG. The
   *  marker preview surfaces this in a small footer when source =
   *  'metadata' and the offset is non-trivial. */
  alignment_offset_s?: number;
  /** `'audio_sync'` when the worker derived the offset via
   *  scipy.signal.correlate between the capture-app's anchor audio
   *  and the video's audio track (highest-priority alignment path,
   *  ROADMAP TRACE-FRAMES-AUDIO-SYNC); `'metadata'` when ffprobe
   *  handed us a usable creation_time + duration; `'fallback'`
   *  when neither path worked and the worker used offset=0. */
  alignment_source?: 'metadata' | 'fallback' | 'audio_sync';
  /** Client-enriched (not from the API): the parent video attachment's
   *  presigned GET URL, copied on during flatten so the trace card can
   *  play a ±5 s clip around this marker. Absent if the video URL
   *  wasn't available. */
  video_url?: string;
}

export interface SessionAttachment {
  id: string;
  session_id: string;
  kind: AttachmentKindRead;
  s3_key: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by_user_id: string | null;
  /** ISO timestamp. */
  created_at: string;
  /**
   * ISO timestamp once the client has confirmed the PUT succeeded.
   * Null = pending (presigned URL minted but no bytes confirmed).
   */
  uploaded_at: string | null;
  /** Populated by GET endpoints when the attachment is a confirmed
   *  video AND the thumbnails worker has produced rows. Empty list
   *  for pending uploads, audio attachments, and legacy rows. */
  thumbs?: AttachmentThumb[];
  /** Short-lived presigned GET for the media FILE (confirmed videos and
   *  confirmed audio — incl. the session anchor-audio) — the trace card
   *  plays a ±5 s window from it. */
  presigned_get_url?: string | null;
  /** Video↔session alignment (seconds): video_time = session_time −
   *  offset. Used to sync a video player to the session-elapsed timeline
   *  (the split editor). Null when audio_sync hasn't run / non-video. */
  audio_sync_offset_s?: number | null;
  /** The edge camera this clip came from, and its label — present on video
   *  rows from an appliance session. Lets the trace offer a per-angle picker
   *  on multi-camera sessions. Null for uploads / audio. */
  camera_id?: string | null;
  camera_name?: string | null;
  /** Coaching sessions only: where the full transcript stands, the
   *  transcript itself (the file's own time, 0 = its first second), and why
   *  it failed or was skipped. Null/absent for every other kind. */
  transcript_status?: CoachingTranscriptStatus | null;
  transcript?: TranscriptSegment[] | null;
  transcript_detail?: string | null;
  /** A recorded debrief's extra camera: the main recording's id
   *  (DEBRIEF-RECORDING). */
  coaching_parent_id?: string | null;
}

export type CoachingTranscriptStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'failed'
  | 'skipped';

export interface TranscriptSegment {
  start_s: number;
  end_s: number;
  text: string;
}

export interface AttachmentCreatePayload {
  kind: AttachmentKind;
  original_filename: string;
  content_type: string;
  size_bytes: number;
}

export interface AttachmentCreateResponse {
  attachment: SessionAttachment;
  presigned_put_url: string;
}

// ---------- Tenant-admin surface ----------
//
// `sessionRowId` is the CaptureSession PK UUID (same shape the
// existing `/sessions/{id}/download` endpoint takes). NOT the
// capture-app-side str(64) session identifier.

export async function listAttachmentsForTenantSession(
  tenantId: string,
  sessionRowId: string,
): Promise<SessionAttachment[]> {
  const { data } = await apiClient.get<SessionAttachment[]>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}/attachments`,
  );
  return data;
}

/** Participant read-only: attachments for a recording in a case the
 *  participant is part of (their own or a teammate's) — Analysis media panel. */
export async function listMyCaseAttachments(
  instanceId: string,
  sessionRowId: string,
): Promise<SessionAttachment[]> {
  const { data } = await apiClient.get<SessionAttachment[]>(
    `/api/v1/tenants/me/cases/${instanceId}/sessions/${sessionRowId}/attachments`,
  );
  return data;
}

export async function createAttachmentForTenantSession(
  tenantId: string,
  sessionRowId: string,
  payload: AttachmentCreatePayload,
): Promise<AttachmentCreateResponse> {
  const { data } = await apiClient.post<AttachmentCreateResponse>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionRowId}/attachments`,
    payload,
  );
  return data;
}

export async function confirmAttachmentForTenant(
  tenantId: string,
  attachmentId: string,
): Promise<SessionAttachment> {
  const { data } = await apiClient.post<SessionAttachment>(
    `/api/v1/tenants/${tenantId}/attachments/${attachmentId}/confirm`,
  );
  return data;
}

/** Transcribe a coaching session again — after a failure, or once
 *  transcription has been switched on for one that was skipped. */
export async function retranscribeCoachingSession(
  tenantId: string,
  attachmentId: string,
): Promise<SessionAttachment> {
  const { data } = await apiClient.post<SessionAttachment>(
    `/api/v1/tenants/${tenantId}/attachments/${attachmentId}/transcribe`,
  );
  return data;
}

/**
 * Clear a pending attachment row (uploaded_at is null). Backend
 * rejects with 409 if the row is already confirmed — the kiosk IAM
 * identity has no s3:DeleteObject so deletion of a confirmed row
 * would orphan the S3 object.
 */
export async function deletePendingAttachmentForTenant(
  tenantId: string,
  attachmentId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/attachments/${attachmentId}`,
  );
}

// ---------- Participant surface ----------

export async function listAttachmentsForMySession(
  sessionRowId: string,
): Promise<SessionAttachment[]> {
  const { data } = await apiClient.get<SessionAttachment[]>(
    `/api/v1/sessions/me/${sessionRowId}/attachments`,
  );
  return data;
}

export async function createAttachmentForMySession(
  sessionRowId: string,
  payload: AttachmentCreatePayload,
): Promise<AttachmentCreateResponse> {
  const { data } = await apiClient.post<AttachmentCreateResponse>(
    `/api/v1/sessions/me/${sessionRowId}/attachments`,
    payload,
  );
  return data;
}

export async function confirmAttachmentForMySession(
  sessionRowId: string,
  attachmentId: string,
): Promise<SessionAttachment> {
  const { data } = await apiClient.post<SessionAttachment>(
    `/api/v1/sessions/me/${sessionRowId}/attachments/${attachmentId}/confirm`,
  );
  return data;
}

/** Participant-side mirror of [[deletePendingAttachmentForTenant]]. */
export async function deletePendingAttachmentForMySession(
  sessionRowId: string,
  attachmentId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/sessions/me/${sessionRowId}/attachments/${attachmentId}`,
  );
}

// ---------- Browser-side S3 PUT ----------

/**
 * Upload one file's bytes to the presigned URL the backend returned.
 * The PUT must send the same Content-Type the backend pinned in the
 * presigned signature; otherwise S3 will reject with
 * SignatureDoesNotMatch.
 *
 * Two failure shapes are surfaced explicitly because the browser
 * eats them silently otherwise:
 *
 *   - fetch() rejects with TypeError → almost always CORS. S3
 *     answered the preflight without a matching
 *     Access-Control-Allow-Origin so the browser dropped the
 *     request before the body went on the wire.
 *   - fetch() resolves with !resp.ok → the request reached S3 and
 *     S3 said no (AccessDenied, SignatureDoesNotMatch, …).
 */
export async function putToPresignedUrl(
  url: string,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
      signal,
    });
  } catch (err: unknown) {
    const detail =
      err instanceof Error ? err.message : String(err);
    throw new Error(
      `S3 PUT was blocked before it left the browser — most likely a ` +
        `missing CORS rule on the bucket. Add an AllowedOrigins entry ` +
        `for this portal's origin. Underlying error: ${detail}`,
    );
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(
      `S3 PUT failed (${resp.status}) ${text.slice(0, 300)}`,
    );
  }
}
